import {
    UploadIcon,
    useAdvertisedApy,
    useStakingStats
} from "@solomon-apps/shared";
import logoSquareBlackBg from "@solomon-apps/shared/src/assets/logos/logo-square-black-bg.svg";
import React, { useCallback, useRef, useState } from "react";

import { createShareCard, RateLimitError } from "../api/shareApi";
import { ChartCard } from "../components/ChartCard";
import { PrimaryChartCard } from "../components/PrimaryChartCard";
import { ShareModal } from "../components/ShareModal";
import { CARD_TYPE_MAP, type ShareCardMeta } from "../data/chartData";
import { useCountdown } from "../hooks/useCountdown";
import { useMetricsCharts } from "../hooks/useMetricsData";
import { formatCurrencyFull } from "../utils/formatters";

// --- Active share card state ---

interface ActiveShareCard {
    title: string;
    value: string;
    delta: string;
    deltaPeriod: string;
}

const SHARE_CARD_CACHE_TTL = 5 * 60 * 1000;

// --- Page ---

export function MetricsPage(): React.ReactElement {
    const { data: stakingStats, isLoading: isStatsLoading } = useStakingStats();
    const {
        apy,
        formattedApy,
        isLoading: isApyLoading
    } = useAdvertisedApy(stakingStats, isStatsLoading);
    const {
        primaryCharts,
        secondaryCharts,
        shareCardMeta,
        isLoading: isChartsLoading
    } = useMetricsCharts();

    const [activeShareCard, setActiveShareCard] =
        useState<ActiveShareCard | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [shareResult, setShareResult] = useState<{
        permalink: string;
        imageUrl: string;
    } | null>(null);
    const [activeTab] = useState<string>("LPs");
    const [hoveredTab, setHoveredTab] = useState<string | null>(null);
    const tabs = ["LPs", "Neobanks", "Treasuries"] as const;

    const {
        secondsLeft: rateLimitSecondsLeft,
        start: startRateLimitCountdown
    } = useCountdown();
    const shareCardCache = useRef<
        Map<string, { permalink: string; imageUrl: string; timestamp: number }>
    >(new Map());

    const handleShare = useCallback(
        async (title: string, kpiValue: number, formattedValue?: string) => {
            const meta = shareCardMeta[title] ?? {
                cardType: (CARD_TYPE_MAP[title] ??
                    "lp-volume") as ShareCardMeta["cardType"],
                delta: "+0",
                deltaPeriod: "last week"
            };
            const cardData = {
                title,
                cardType: meta.cardType,
                value: formattedValue ?? formatCurrencyFull(kpiValue),
                delta: meta.delta,
                deltaPeriod: meta.deltaPeriod
            };

            setActiveShareCard(cardData);
            setModalOpen(true);

            if (rateLimitSecondsLeft > 0) return;

            const cacheKey = JSON.stringify(cardData);
            const cached = shareCardCache.current.get(cacheKey);
            if (
                cached &&
                Date.now() - cached.timestamp < SHARE_CARD_CACHE_TTL
            ) {
                setShareResult({
                    permalink: cached.permalink,
                    imageUrl: cached.imageUrl
                });
                return;
            }

            setIsLoading(true);
            setShareResult(null);

            try {
                const result = await createShareCard(cardData);
                setShareResult(result);
                shareCardCache.current.set(cacheKey, {
                    ...result,
                    timestamp: Date.now()
                });
            } catch (err) {
                if (err instanceof RateLimitError) {
                    startRateLimitCountdown(err.retryAfter);
                } else {
                    console.error("Failed to create share card:", err);
                }
            } finally {
                setIsLoading(false);
            }
        },
        [rateLimitSecondsLeft, startRateLimitCountdown, shareCardMeta]
    );

    const handleClose = useCallback(() => {
        setModalOpen(false);
        setActiveShareCard(null);
        setShareResult(null);
    }, []);

    const handleDownload = useCallback(async () => {
        if (!shareResult || !activeShareCard) return;

        const slug = activeShareCard.title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-");
        const filename = `solomon-${slug}.png`;

        const response = await fetch(shareResult.imageUrl);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
    }, [shareResult, activeShareCard]);

    const handleShareX = useCallback(() => {
        if (!activeShareCard || !shareResult) return;

        const tweetTextMap: Record<string, string> = {
            "LP Volume (Cumulative)": "Live Solomon YaaS stats: LP Volume",
            "LP Earnings (Cumulative)": "Live Solomon YaaS stats: LP Earnings",
            "Total Positions Using YaaS":
                "Live Solomon YaaS stats: Total LP Positions",
            "YaaS APY": `${activeShareCard.value} APY on Solomon YaaS right now`
        };
        const text =
            tweetTextMap[activeShareCard.title] ??
            `Live Solomon YaaS stats: ${activeShareCard.title}`;

        const tweetUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareResult.permalink)}`;
        window.open(tweetUrl, "_blank", "noopener,noreferrer");
    }, [activeShareCard, shareResult]);

    return (
        <div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 md:mb-10">
                <div className="flex items-center gap-3 md:gap-4 mb-2 md:mb-0">
                    <img
                        src={logoSquareBlackBg}
                        alt="Solomon logo"
                        className="w-10 h-10 md:w-[84px] md:h-[84px]"
                    />
                    <h1 className="rhymes-title text-2xl sm:text-3xl lg:text-5xl font-extralight text-m-foreground">
                        YaaS Metrics for LP Pools
                    </h1>
                </div>

                <button
                    onClick={() => {
                        void handleShare("YaaS APY", apy ?? 0, formattedApy);
                    }}
                    disabled={isApyLoading}
                    className="group inline-flex items-center gap-4 text-xl lg:text-3xl px-3 py-1 md:p-6 text-m-foreground bg-m-surface-muted cursor-pointer disabled:cursor-not-allowed"
                    aria-label="Share YaaS APY"
                >
                    <span>YaaS APY</span>
                    {isApyLoading ? (
                        <span className="inline-block h-[1em] w-16 bg-m-foreground/20 animate-pulse align-middle" />
                    ) : (
                        formattedApy
                    )}
                    <UploadIcon className="w-5 h-5 md:w-8 md:h-8 text-gray-400 group-hover:text-m-accent" />
                </button>
            </div>
            <div className="flex gap-3 mb-4 md:mb-6">
                {tabs.map((tab) => {
                    const isActive = tab === activeTab;
                    return (
                        <button
                            key={tab}
                            type="button"
                            className={
                                isActive
                                    ? "flex-1 md:flex-none md:w-48 py-2 text-base md:text-lg font-normal bg-m-foreground text-m-surface-base cursor-default"
                                    : "flex-1 md:flex-none md:w-48 py-2 text-base md:text-lg font-normal bg-transparent border border-m-foreground-muted text-m-foreground-muted cursor-default"
                            }
                            onMouseEnter={() => {
                                if (!isActive) setHoveredTab(tab);
                            }}
                            onMouseLeave={() => setHoveredTab(null)}
                        >
                            {isActive ? (
                                <span className="border-b border-m-surface-base">
                                    {tab}
                                </span>
                            ) : hoveredTab === tab ? (
                                "Coming Soon"
                            ) : (
                                tab
                            )}
                        </button>
                    );
                })}
            </div>

            {activeTab === "LPs" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                    {isChartsLoading ? (
                        <>
                            {[...Array(3)].map((_, i) => (
                                <div
                                    key={i}
                                    className="bg-m-surface-base animate-pulse h-[280px] sm:h-80"
                                />
                            ))}
                        </>
                    ) : (
                        <>
                            {primaryCharts.map((chart) => (
                                <PrimaryChartCard
                                    key={chart.title}
                                    title={chart.title}
                                    data={chart.data}
                                    kpiValue={chart.kpiValue}
                                    formatter={chart.formatter}
                                    onShare={() => {
                                        void handleShare(
                                            chart.title,
                                            chart.kpiValue
                                        );
                                    }}
                                />
                            ))}
                            {secondaryCharts.map((chart) => (
                                <ChartCard
                                    key={chart.title}
                                    title={chart.title}
                                    data={chart.data}
                                    kpiValue={chart.kpiValue}
                                    formatter={chart.formatter}
                                    onShare={() => {
                                        void handleShare(
                                            chart.title,
                                            chart.kpiValue
                                        );
                                    }}
                                />
                            ))}
                        </>
                    )}
                </div>
            )}

            {/* Share modal */}
            <ShareModal
                isOpen={modalOpen}
                onClose={handleClose}
                imageUrl={shareResult?.imageUrl ?? null}
                title={activeShareCard?.title ?? ""}
                isLoading={isLoading}
                onDownload={handleDownload}
                onShareX={handleShareX}
                rateLimitSecondsLeft={rateLimitSecondsLeft}
            />
        </div>
    );
}
